/**
 * @module events/listeners/builtins/tap
 */

/**
 * @typedef {module:utils/model.Model} Model
 * @typedef {module:events/listeners/listener.Listener} Listener
 * @typedef {Model|Listener} Tap
 * @typedef {Tap} TapDown
 * @typedef {Tap} TapUp
 */
import array from '../../../utils/array.js'
import object from '../../../utils/object.js'
import listener from '../listener.js'
import click from '../../types/click.js'
import mouseDown from '../../types/mouseDown.js'
import mouseUp from '../../types/mouseUp.js'
import scroll from '../../types/scroll.js'
import touchEnd from '../../types/touchEnd.js'
import touchStart from '../../types/touchStart.js'
import preventDefault from '../../hooks/preventDefault.js'
import stopImmediatePropagation from '../../hooks/stopImmediatePropagation.js'
import capture from '../../options/capture.js'
import once from '../../options/once.js'
import passive from '../../options/passive.js'

const listeners = new WeakMap()

const events = new WeakMap()

const { listen, forget } = listener

const taps = object(null, {
  'tap-down': [mouseDown, touchStart],
  'tap-up': [mouseUp, touchEnd]
})

const touches = array([touchStart, touchEnd])

const types = array(Object.keys(taps))

const [, up] = types

const handle = (handler, event) => {
  const { currentTarget, type } = event
  const { scrolls } = onScroll
  const { size } = scrolls

  if (size || touches.includes(type)) {
    return
  }
  
  events.set(currentTarget, event)
  currentTarget.focus?.()
  
  return handler.handleEvent(event)
}

const init = (handler, [mouse, touch]) => [
  object(handler, {
    type: mouse,
    async handleEvent (event) {
      const { button, buttons } = event
      
      if (!button && buttons < 2) {
        return handle(handler, event)
      }
    },
    listen,
    forget
  }),
  object(handler, {
    type: touch,
    async handleEvent (event) {
      const { changedTouches, touches } = event

      if (handler === onTapDown || (changedTouches.length === 1 && !touches.length)) {
        return handle(handler, event)
      }
    },
    listen,
    forget
  })
]

const onClick = object(listener, {
  type: click,
  capture,
  task: (target, click) => {
    if (!events.has(target)) {
      return
    }
    
    const event = events.get(target)
    const { type } = event
    
    if (event.defaultPrevented) {
      preventDefault(click)
    }
    
    if (!event.bubbles) {
      stopImmediatePropagation(click)
    }
    
    if (type.endsWith('up')) {
      events.delete(target)
    }
  }
})

const onScroll = object(listener, {
  type: scroll,
  capture,
  once,
  passive,
  scrolls: new Set(),
  task () {
    this.scrolls.add(true)
  }
})

/**
 * @type {Tap}
 */
const tap = object(listener, {
  listen (target) {
    if (!listeners.has(this)) {
      const { type } = this

      if (type === up) {
        const { ownerDocument = target } = target
        const { defaultView } = ownerDocument ?? target
        const window = defaultView ?? target
        
        onTapDown.listen(window)
      }
      
      listeners.set(this, init(this, taps[type]))
    }
    
    const [onMouse, onTouch] = listeners.get(this)
    
    onTouch.listen(target)
    
    if (this !== onTapDown) {
      onMouse.listen(target)
      onClick.listen(target)
    }
    
    listener.listen.call(this, target)
  },
  forget (target) {
    const [onMouse, onTouch] = listeners.get(this) ?? []
    
    onTouch?.forget(target)
    
    if (this !== onTapDown) {
      onMouse?.forget(target)
      onClick.forget(target)
    }
    
    listener.forget.call(this, target)
  }
})

/**
 * @type {TapDown}
 */
export const tapDown = object(tap, {
  type: 'tap-down'
})

/**
 * @type {TapUp}
 */
export const tapUp = object(tap, {
  type: 'tap-up'
})

const onTapDown = object(tapDown, {
  capture,
  passive,
  task: defaultView => {
    const { scrolls } = onScroll
    
    scrolls.clear()
    onScroll.listen(defaultView)
  }
})
